import axiosInstance from '../config/axios.js';

const API_BASE = '/shipment-qr-status';

const appendScanFile = (formData, file) => {
  if (!(file instanceof File)) return;
  const isPdf =
    file.type === 'application/pdf' || (file.name || '').toLowerCase().endsWith('.pdf');
  formData.append(isPdf ? 'scanned_pdf' : 'scanned_image', file, file.name);
};

const appendPayload = (formData, payload = {}) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (key === 'shipment_ids' && Array.isArray(value)) {
      formData.append('shipment_ids', JSON.stringify(value));
      return;
    }
    if (
      (key === 'receiver_agent_by_shipment' || key === 'verified_units_by_shipment')
      && value
      && typeof value === 'object'
    ) {
      formData.append(key, JSON.stringify(value));
      return;
    }
    formData.append(key, value);
  });
};

const QR_UPLOAD_TIMEOUT_MS = 120000;

const shipmentQrStatusService = {
  resolveFromQr: async (fileOrShipmentId) => {
    if (fileOrShipmentId instanceof File) {
      const formData = new FormData();
      appendScanFile(formData, fileOrShipmentId);
      const response = await axiosInstance.post(`${API_BASE}/resolve`, formData, {
        headers: { 'Content-Type': undefined },
        timeout: QR_UPLOAD_TIMEOUT_MS,
      });
      return response.data;
    }

    const response = await axiosInstance.post(`${API_BASE}/resolve`, {
      shipment_id: fileOrShipmentId,
    });
    return response.data;
  },

  /** Resolve from app live QR decode (raw QR string or shipment id). */
  resolveFromQrData: async ({ qrData, shipmentId, shipmentIds, unitNo } = {}) => {
    const payload = {};
    // Always send raw QR when present so parcel unit (?u=N) is not dropped.
    if (qrData) payload.qr_data = qrData;
    if (shipmentIds?.length) payload.shipment_ids = shipmentIds;
    else if (shipmentId) payload.shipment_id = shipmentId;
    if (unitNo != null && unitNo !== '') payload.unit_no = unitNo;
    if (!payload.qr_data && !payload.shipment_id && !payload.shipment_ids) {
      throw new Error('No QR data to resolve');
    }

    const response = await axiosInstance.post(`${API_BASE}/resolve`, payload);
    return response.data;
  },

  updateStatusFromQr: async (payload = {}, extraFiles = {}) => {
    const deliveryImage = extraFiles.delivery_receiver_image;
    const govIdImage = extraFiles.delivery_gov_id_image;
    const hasFile = deliveryImage instanceof File || govIdImage instanceof File;

    if (hasFile) {
      const formData = new FormData();
      appendPayload(formData, payload);
      if (deliveryImage instanceof File) {
        formData.append('delivery_receiver_image', deliveryImage);
      }
      if (govIdImage instanceof File) {
        formData.append('delivery_gov_id_image', govIdImage);
      }
      const response = await axiosInstance.post(`${API_BASE}/update-status`, formData, {
        headers: { 'Content-Type': undefined },
        timeout: QR_UPLOAD_TIMEOUT_MS,
      });
      return response.data;
    }

    const response = await axiosInstance.post(`${API_BASE}/update-status`, payload);
    return response.data;
  },

  listTodayMlSheets: async (params = {}) => {
    const response = await axiosInstance.get(`${API_BASE}/today-ml-sheets`, { params });
    return response.data;
  },

  dispatchOntoMl: async ({
    shipment_ids,
    ml_sheet_id,
    journey_date,
    verified_units_by_shipment,
    allow_legacy_qr,
  }) => {
    const response = await axiosInstance.post(`${API_BASE}/dispatch`, {
      shipment_ids,
      ml_sheet_id,
      journey_date,
      verified_units_by_shipment,
      allow_legacy_qr: allow_legacy_qr ? true : undefined,
    });
    return response.data;
  },

  validateDispatch: async ({ shipment_id, ml_sheet_id, journey_date }) => {
    const response = await axiosInstance.post(`${API_BASE}/validate-dispatch`, {
      shipment_id,
      ml_sheet_id,
      journey_date,
    });
    return response.data;
  },
};

export default shipmentQrStatusService;
